import React, { useState, useRef, useMemo, useEffect } from "react";
import { ContentHeader } from "@components";
import DataTable from "../components/datatable/DataTable";
import API from "../api";
import toastr from "toastr";

interface FinanceRecord {
  id: number;
  rowNumber?: number;
  entry_date: string;
  pay_for: string;
  payment_method: string;
  reference_no: string;
  type: "Income" | "Expense";
  amount: number;
}

// Totals come from the backend (all pages), not just the current page.
interface FinanceTotals {
  incomeTotal: number;
  expenseTotal: number;
}

const FinanceReport: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });

  const [totals, setTotals] = useState<FinanceTotals>({
    incomeTotal: 0,
    expenseTotal: 0,
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const PAGE_SIZE = 25;

  const tableRef = useRef<any>(null);

  // Refetch whenever dates change
  useEffect(() => {
    tableRef.current?.refetch();
  }, [startDate, endDate]);

  const fetchFinanceData = async (
    page: number,
    search: string,
    sortBy    = "entry_date",
    sortOrder = "desc"
  ) => {
    try {
      const res = await API.get(
        `/finance/getFinanceReport?page=${page}&pageSize=25` +
        `&search=${encodeURIComponent(search)}` +
        `&sortBy=${sortBy}&sortOrder=${sortOrder}` +
        `&startDate=${startDate}&endDate=${endDate}`
      );

      const rawData: FinanceRecord[] = res.data.data  || [];
      const total: number            = res.data.total || 0;
      setCurrentPage(page);

      // Inject serial numbers based on page offset
      const offset = (page - 1) * PAGE_SIZE;
      const data = rawData.map((item, idx) => ({
        ...item,
        rowNumber: offset + idx + 1,
      }));

      // ── Fetch totals for ALL pages using a large pageSize ──────────────────
      // Only do this on the first page to avoid multiple heavy requests.
      if (page === 1) {
        try {
          const totalsRes = await API.get(
            `/finance/getFinanceReport?page=1&pageSize=100000` +
            `&search=${encodeURIComponent(search)}` +
            `&sortBy=id&sortOrder=asc` +
            `&startDate=${startDate}&endDate=${endDate}`
          );
          const allData: FinanceRecord[] = totalsRes.data.data || [];
          let income  = 0;
          let expense = 0;
          allData.forEach((item) => {
            if (item.type === "Income")  income  += Number(item.amount || 0);
            if (item.type === "Expense") expense += Number(item.amount || 0);
          });
          setTotals({ incomeTotal: income, expenseTotal: expense });
        } catch {
          // Totals fetch failed; keep previous values
        }
      }

      return { data, total };
    } catch (err: any) {
      console.error("fetchFinanceData error:", err);
      toastr.error(err?.response?.data?.message || "Failed to fetch finance data");
      return { data: [], total: 0 };
    }
  };

  const handleDateFilter = (e: React.FormEvent) => {
    e.preventDefault();
    if (new Date(endDate) < new Date(startDate)) {
      toastr.warning("End date should be greater than Start date");
      return;
    }
    tableRef.current?.refetch();
  };

  // ── Helper: trigger a browser download from a Blob ──────────────────────────
  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // ── Print: builds a printable HTML view client-side from the full dataset ──
  const handlePrint = async () => {
    try {
      const res = await API.get(
        `/finance/getFinanceReport?page=1&pageSize=100000&sortBy=entry_date&sortOrder=asc` +
        `&startDate=${startDate}&endDate=${endDate}`
      );
      const rows: FinanceRecord[] = res.data.data || [];

      let income = 0;
      let expense = 0;

      const bodyRows = rows
        .map((item, idx) => {
          const isIncome = item.type === "Income";
          const amount = Number(item.amount || 0);
          if (isIncome) income += amount; else expense += amount;
          const dateStr = item.entry_date
            ? new Date(item.entry_date).toLocaleDateString("en-GB")
            : "-";
          return `
            <tr>
              <td>${idx + 1}</td>
              <td>${dateStr}</td>
              <td>${item.pay_for ?? ""}</td>
              <td>${item.payment_method ?? ""}</td>
              <td>${item.reference_no ?? ""}</td>
              <td style="text-align:center">${isIncome ? "Rs. " + amount.toFixed(2) : ""}</td>
              <td style="text-align:center">${!isIncome ? "Rs. " + amount.toFixed(2) : ""}</td>
            </tr>`;
        })
        .join("");

      const net = income - expense;
      const netLabel = net >= 0 ? "Profit Amount" : "Loss Amount";
      const netColor = net >= 0 ? "#00BB00" : "#FF0000";

      const html = `
        <html>
          <head>
            <title>Finance Report</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h2, p { text-align: center; margin: 4px 0; }
              table { width: 100%; border-collapse: collapse; margin-top: 12px; }
              th, td { border: 1px solid #333; padding: 6px 8px; font-size: 12px; }
              th { background: #f0f0f0; }
              tfoot td, tfoot th { font-weight: bold; }
            </style>
          </head>
          <body>
            <h2>Finance Report</h2>
            <p>From ${startDate} to ${endDate}</p>
            <table>
              <thead>
                <tr>
                  <th>ID</th><th>Date</th><th>Description</th><th>Payment Method</th>
                  <th>Reference No.</th><th>Income</th><th>Expense</th>
                </tr>
              </thead>
              <tbody>${bodyRows}</tbody>
              <tfoot>
                <tr>
                  <td colspan="4"></td>
                  <td style="text-align:center">Total</td>
                  <td style="text-align:center">Rs. ${income.toFixed(2)}</td>
                  <td style="text-align:center">Rs. ${expense.toFixed(2)}</td>
                </tr>
                <tr>
                  <td colspan="4"></td>
                  <th style="text-align:center">${netLabel}</th>
                  <td></td>
                  <td style="text-align:center;color:${netColor}">Rs. ${Math.abs(net).toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
            <script>
              window.onload = function () { window.print(); };
            </script>
          </body>
        </html>`;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
      } else {
        toastr.warning("Please allow popups to print the report");
      }
    } catch (err: any) {
      console.error("handlePrint error:", err);
      toastr.error("Failed to generate print view");
    }
  };

  // ── PDF: downloads a server-generated PDF ───────────────────────────────────
  const handlePDF = async () => {
    try {
      const res = await API.get(
        `/finance/exportFinancePDF?startDate=${startDate}&endDate=${endDate}`,
        { responseType: "blob" }
      );
      downloadBlob(res.data, `finance-report-${startDate}-to-${endDate}.pdf`);
    } catch (err: any) {
      console.error("handlePDF error:", err);
      toastr.error("Failed to generate PDF");
    }
  };

  // ── Excel: downloads a server-generated XLSX ────────────────────────────────
  const handleExcel = async () => {
    try {
      const res = await API.get(
        `/finance/exportFinanceExcel?startDate=${startDate}&endDate=${endDate}`,
        { responseType: "blob" }
      );
      downloadBlob(res.data, `finance-report-${startDate}-to-${endDate}.xlsx`);
    } catch (err: any) {
      console.error("handleExcel error:", err);
      toastr.error("Failed to generate Excel file");
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "rowNumber",
        cell: ({ row }: any) => row.original.rowNumber ?? row.original.id,
      },
      {
        header: "Date",
        accessorKey: "entry_date",
        cell: ({ row }: any) => {
          const date = row.original.entry_date;
          return date ? new Date(date).toLocaleDateString("en-GB") : "-";
        },
      },
      { header: "Description",     accessorKey: "pay_for" },
      { header: "Payment Method",  accessorKey: "payment_method" },
      { header: "Reference No.",   accessorKey: "reference_no" },
      {
        header: "Income",
        accessorKey: "income_amount",   // unique key — avoid duplicate accessorKey
        cell: ({ row }: any) => {
          const item = row.original;
          if (item.type === "Income") {
            return (
              <span style={{ textAlign: "center", display: "block" }}>
                Rs. {Number(item.amount || 0).toFixed(2)}
              </span>
            );
          }
          return <span>&nbsp;</span>;
        },
      },
      {
        header: "Expense",
        accessorKey: "expense_amount",  // unique key
        cell: ({ row }: any) => {
          const item = row.original;
          if (item.type === "Expense") {
            return (
              <span style={{ textAlign: "center", display: "block" }}>
                Rs. {Number(item.amount || 0).toFixed(2)}
              </span>
            );
          }
          return <span>&nbsp;</span>;
        },
      },
    ],
    [currentPage]
  );

  const { incomeTotal, expenseTotal } = totals;
  const netTotal  = incomeTotal - expenseTotal;
  const isProfit  = netTotal >= 0;
  const netLabel  = isProfit ? "Profit Amount" : "Loss Amount";
  const netColor  = isProfit ? "#00BB00" : "#FF0000";

  return (
    <div>
      <ContentHeader title="Finance Report" />

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">

            {/* ── Header: date filter + export buttons ── */}
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <form
                  onSubmit={handleDateFilter}
                  className="d-flex gap-2 align-items-center flex-wrap"
                >
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ width: "150px" }}
                  />
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ width: "150px" }}
                  />
                  <button type="submit" className="btn btn-light" title="Search">
                    <em className="fa fa-search" />
                  </button>
                </form>

                <div className="btn-group">
                  <button className="btn btn-light "    onClick={handlePrint}  title="Print">
                    <em className="fa fa-print" /> Print
                  </button>
                  <button className="btn btn-light"  onClick={handlePDF}    title="PDF">
                    <em className="fa fa-file-pdf" /> PDF
                  </button>
                  <button className="btn btn-light" onClick={handleExcel}  title="Excel">
                    <em className="fa fa-file-excel" /> Excel
                  </button>
                </div>
              </div>
            </div>

            {/* ── Data table ── */}
            <div className="card-body">
              <DataTable
                ref={tableRef}
                columns={columns}
                queryFn={fetchFinanceData}
                queryKey={["finance-report", startDate, endDate]}
              />
            </div>

            {/* ── Footer totals — mirrors PHP tfoot ── */}
            <div className="card-footer bg-light">
              <div className="table-responsive">
                <table className="table table-bordered mb-0">
                  <tbody>
                    {/* Total row */}
                    <tr style={{ fontWeight: "bold" }}>
                      <td style={{ textAlign: "center" }}>&nbsp;</td>
                      <td style={{ textAlign: "center" }}>&nbsp;</td>
                      <td style={{ textAlign: "center" }}>&nbsp;</td>
                      <td style={{ textAlign: "center" }}>&nbsp;</td>
                      <td style={{ textAlign: "center", fontWeight: "bold" }}>Total</td>
                      <td style={{ textAlign: "center", fontWeight: "bold" }}>
                        Rs. {incomeTotal.toFixed(2)}
                      </td>
                      <td style={{ textAlign: "center", fontWeight: "bold" }}>
                        Rs. {expenseTotal.toFixed(2)}
                      </td>
                    </tr>
                    {/* Profit / Loss row */}
                    <tr style={{ fontWeight: "bold" }}>
                      <td style={{ textAlign: "center" }}>&nbsp;</td>
                      <td style={{ textAlign: "center" }}>&nbsp;</td>
                      <td style={{ textAlign: "center" }}>&nbsp;</td>
                      <td style={{ textAlign: "center" }}>&nbsp;</td>
                      <th style={{ textAlign: "center", fontWeight: "bolder" }}>{netLabel}</th>
                      <td style={{ textAlign: "center" }}>&nbsp;</td>
                      <td style={{ textAlign: "center", color: netColor, fontWeight: "bold" }}>
                        Rs. {Math.abs(netTotal).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default FinanceReport;